import React, { useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import ReactTooltip from "react-tooltip";
import Table from "../common/components/Table";
import refreshIcon from "../../assets/img/img-refresh.svg";
import { useDispatch, useSelector } from "react-redux";
import actions from "./actions";
import moment from "moment";
import { cloneDeep, isEqual } from "lodash";
import ConfirmationModal from "../common/components/ConfirmationModal";
import Portal from "../common/components/Portal";
import LoadingOverlay from "react-loading-overlay";
import Loader from "../common/components/Loader";
import { checkPermission, showAlert as showAlertUtil } from "../../config/utils";
import TableTopIcons from "../common/components/TableTopIcons";
import { generalTaskManagerTableData } from "./constants";
import ErrorLog from "./components/errorLog";
import { downloadPresignedUrlFile, getPresignedUrl, getTableConfig, saveTableConfig } from "../common/actions";
import { DynamicColumnModal } from "../common/DynamicColumnModal";
import { computeTableConfig, getPinnedColumnLeftPositions } from "../../utils/tableUtils";
import "./taskManager.css";

const TASK_MANAGER_TABLE_TYPE = "TASK_MANAGER";

const GeneralTaskManager = () => {
    const deleteRef = useRef(null);
    const cancelRef = useRef(null);
    const pinnedColumnsRef = useRef({});
    const tableRef = useRef(null);
    const dispatch = useDispatch();
    const { entityParams, getTaskManagerListResponse, getListForCommonFilterResponse, deleteTaskManagerResponse, cancelTaskManagerResponse } =
        useSelector(state => state.taskManagerReducer) || {};
    const [tableData, setTableData] = useState({
        keys: generalTaskManagerTableData.keys,
        data: [],
        config: entityParams?.tableConfig || cloneDeep(generalTaskManagerTableData.config)
    });
    const [confirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [selectedDeleteTaskById, setSelectedDeleteTaskById] = useState(false);
    const [showConfirmEndTask, setShowConfirmEndTask] = useState(false);
    const [selectedEndTask, setSelectedEndTask] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [showWildCardFilter, setShowWildCardFilter] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [params, setParams] = useState(entityParams?.params || {});
    const [showLogModal, setShowLogModal] = useState(false);
    const [paginationParams, setPaginationParams] = useState(entityParams?.paginationParams ?? {});
    const [tableConfigChanged, setTableConfigChanged] = useState(false);
    const [tableConfigLoading, setTableConfigLoading] = useState(false);
    const [searchField, setSearchField] = useState(null);
    const [highlightSearchField, setHighlightSearchField] = useState(null);

    useEffect(() => {
        setTableDataConfig();
    }, []);

    useEffect(() => {
        refreshTaskManagerList();
    }, [params]);

    useEffect(() => {
        updateTaskManagerEntityParams();
    }, [params, paginationParams]);

    useEffect(() => {
        if (getTaskManagerListResponse?.task_manager_data) {
            const formattedData = getTaskManagerListResponse?.task_manager_data?.map(item => {
                return {
                    ...item,
                    created_at: item?.created_at
                        ? moment(item?.created_at).isValid()
                            ? moment(item?.created_at).format("MM-DD-YYYY hh:mm A")
                            : null
                        : null,
                    date_done: item?.date_done
                        ? moment(item?.date_done).isValid()
                            ? moment(item?.date_done).format("MM-DD-YYYY hh:mm A")
                            : null
                        : null,
                    cancel_requested_at: item?.cancel_requested_at
                        ? moment(item?.cancel_requested_at).isValid()
                            ? moment(item?.cancel_requested_at).format("MM-DD-YYYY hh:mm A")
                            : null
                        : null,
                    updated_at: item?.updated_at
                        ? moment(item?.updated_at).isValid()
                            ? moment(item?.updated_at).format("MM-DD-YYYY hh:mm A")
                            : null
                        : null
                };
            });

            setTableData(prevTableData => ({
                keys: prevTableData.keys,
                data: formattedData,
                config: prevTableData.config
            }));
            setPaginationParams(prevPaginationParams => ({
                ...prevPaginationParams,
                totalCount: getTaskManagerListResponse?.count,
                totalPages: Math.ceil(getTaskManagerListResponse?.count / (prevPaginationParams.perPage ?? 100))
            }));
        } else if (typeof getTaskManagerListResponse?.detail === "string") setAlertMessage(getTaskManagerListResponse?.detail);
    }, [getTaskManagerListResponse]);

    useEffect(() => {
        alertMessage && showAlert();
    }, [alertMessage]);
    useEffect(() => {
        if (deleteRef?.current && deleteTaskManagerResponse) {
            setSelectedDeleteTaskById(null);
            setShowConfirmDeleteModal(false);
            deleteRef.current = false;
            setAlertMessage(deleteTaskManagerResponse?.message || deleteTaskManagerResponse?.detail);
            refreshTaskManagerList();
        }
    }, [deleteTaskManagerResponse]);
    useEffect(() => {
        if (cancelRef?.current) {
            refreshTaskManagerList();
            setSelectedEndTask(null);
            setShowConfirmEndTask(false);
            cancelRef.current = false;
            setAlertMessage(cancelTaskManagerResponse?.message || cancelTaskManagerResponse?.detail);
        }
    }, [cancelTaskManagerResponse]);

    const showAlert = () => {
        showAlertUtil(alertMessage);
        setTimeout(() => setAlertMessage(""), 3000);
    };

    const refreshTaskManagerList = () => {
        const { limit, offset, search, order, filters = {}, list = {} } = params;
        const apiPayload = {
            limit,
            offset: offset + 1,
            search,
            order: order ?? { "fca_task_manager.created_at": "desc" },
            filters,
            list
        };

        dispatch(actions.getTaskManagerList(apiPayload));
    };

    const resetFilter = () => {
        setParams({
            ...params,
            offset: 0,
            limit: 100,
            search: "",
            order: {},
            list: {},
            fe_list_label: null
        });
        setPaginationParams(prevPaginationParams => ({
            ...prevPaginationParams,
            perPage: 100,
            currentPage: 0
        }));
        setShowWildCardFilter(false);
    };

    const resetTableAllFilter = async () => {
        setParams(prevParams => ({
            ...prevParams,
            filters: null,
            exportParams: null,
            list: {},
            fe_list_label: null
        }));
    };

    const handlePageClick = selected => {
        setParams(prevParams => ({
            ...prevParams,
            offset: selected?.selected
        }));
        setPaginationParams(prevPaginationParams => ({
            ...prevPaginationParams,
            currentPage: selected?.selected
        }));
    };

    const handlePerPageChange = e => {
        const newPerPage = e.target.value;

        setParams(prevParams => ({
            ...prevParams,
            limit: newPerPage,
            offset: 0
        }));
        setPaginationParams(prevPaginationParams => ({
            ...prevPaginationParams,
            perPage: newPerPage,
            currentPage: 0
        }));
    };

    const toggleWildCardFilter = () => {
        setShowWildCardFilter(!showWildCardFilter);
    };

    const resetSort = async () => {
        setParams(prevParams => ({
            ...prevParams,
            order: {}
        }));
    };
    const changeFilterLabel = (dataValue, keyName, searchKey) => {
        const extracted = {};
        Object.entries(dataValue || {}).forEach(([key, value]) => {
            const configEntry = Object.values(tableData?.config || {}).find(item => {
                return item?.searchKey === key || (item?.searchKey ?? "")?.split(".")?.[1] === key;
            });
            const filteredList = getListForCommonFilterResponse?.list
                ?.filter(item => value?.includes(item.id) || value?.includes(item.name))
                ?.map(item => item.name);
            if (configEntry?.label) {
                extracted[configEntry.label] =
                    filteredList?.length > 0
                        ? filteredList
                        : (searchKey ?? "")?.split(".")?.[1] !== key
                          ? params?.fe_list_label?.[configEntry?.label]
                          : value;
            }
        });
        return extracted;
    };

    const updateCommonFilter = async (list, exportParams, key, searchKey) => {
        setParams(prevParams => ({
            ...prevParams,
            offset: 0,
            list,
            exportParams: list ? exportParams : undefined,
            fe_list_label: changeFilterLabel(exportParams, key, searchKey)
        }));
        setPaginationParams(prevPaginationParams => ({
            ...prevPaginationParams,
            currentPage: 0
        }));
    };

    const updateWildCardFilter = async newFilter => {
        setParams(prevParams => ({
            ...prevParams,
            offset: 0,
            filters: newFilter
        }));
        setPaginationParams(prevPaginationParams => ({
            ...prevPaginationParams,
            currentPage: 0
        }));
    };

    const updateTableSortFilters = async (searchKey, a, b) => {
        setParams(prevParams => ({
            ...prevParams,
            order: {
                ...prevParams.order,
                [searchKey]: prevParams.order?.[searchKey] === "desc" ? "asc" : "desc"
            }
        }));
    };

    const updateLastSortFilter = async searchKey => {
        setParams(prevParams => ({
            ...prevParams,
            order: {
                [searchKey]: prevParams.order?.[searchKey] === "asc" ? "desc" : "asc"
            }
        }));
    };

    const handleGlobalSearch = async search => {
        setParams({
            ...params,
            offset: 0,
            search
        });
        setPaginationParams(prevPaginationParams => ({
            ...prevPaginationParams,
            currentPage: 0
        }));
    };

    const getListForCommonFilter = async paramsVal => {
        const remainingExportParams = { ...(params?.list ?? {}) };
        delete remainingExportParams[paramsVal?.searchKey];
        const requestParams = {
            ...paramsVal,
            search: params?.search,
            filters: params?.filters,
            list: remainingExportParams
        };

        return await dispatch(actions.getListForCommonFilter(requestParams));
    };

    const updateTaskManagerEntityParams = async (updatedTableConfig = tableData?.config) => {
        let entityParams = {
            params: params,
            tableConfig: updatedTableConfig,
            paginationParams
        };
        dispatch(actions.updateTaskManagerEntityParams(entityParams));
    };

    const saveUserTableConfig = async (config, isReset, keys) => {
        await saveTableConfig({
            user_id: localStorage.getItem("userId"),
            table_type: TASK_MANAGER_TABLE_TYPE,
            table_settings: isReset ? null : { keys: keys || tableData.keys, config: config || tableData.config }
        });
        setTableConfigChanged(isReset ? false : true);
    };

    const setTableDataConfig = async () => {
        const tableSettings =
            (
                await getTableConfig({
                    user_id: localStorage.getItem("userId"),
                    table_type: TASK_MANAGER_TABLE_TYPE
                })
            )?.data?.table_settings || null;
        const { config: computedConfig, keys: computedKeys } = computeTableConfig({
            defaultConfig: generalTaskManagerTableData.config,
            defaultKeys: generalTaskManagerTableData.keys || [],
            tableSettings
        });
        setTableData(prevTableData => ({
            ...prevTableData,
            keys: computedKeys,
            config: computedConfig
        }));
        setTableConfigChanged(!!tableSettings);
        updateTaskManagerEntityParams(computedConfig);
    };

    const resetAllTableConfig = async () => {
        setTableConfigLoading(true);
        await saveUserTableConfig(null, true);
        await setTableDataConfig();
        setTableConfigLoading(false);
    };

    const handleHideColumn = async tableConfig => {
        setTableConfigLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1));
        const { keys, config } = tableConfig;

        Object.keys(config)
            .filter(key => !config[key]?.isVisible && config[key]?.pinned)
            .forEach(key => {
                const { left, ...remainingStyles } = config[key]?.style || {};
                config[key] = {
                    ...config[key],
                    pinned: false,
                    style: remainingStyles
                };
            });

        const pinnedColumns = keys.filter(key => config[key]?.isVisible && config[key]?.pinned);
        const columnLeftValues = getPinnedColumnLeftPositions(pinnedColumns, pinnedColumnsRef.current);

        pinnedColumns.forEach(key => {
            config[key] = {
                ...config[key],
                style: {
                    ...config[key]?.style,
                    left: columnLeftValues[key] || "auto"
                }
            };
        });

        setTableData(prevTableData => ({
            ...prevTableData,
            keys,
            config
        }));
        await saveUserTableConfig(config, false, keys);
        updateTaskManagerEntityParams(config);
        setTableConfigLoading(false);
    };

    const handleColumnPin = async keyItem => {
        const { config, keys } = tableData;
        setTableConfigLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1));
        const { left, ...remainingStyles } = config[keyItem]?.style || {};

        const updatedConfig = {
            ...config,
            [keyItem]: {
                ...config[keyItem],
                pinned: !config[keyItem]?.pinned,
                style: config[keyItem]?.pinned
                    ? remainingStyles
                    : {
                          ...remainingStyles,
                          left: config[keyItem]?.style?.left || "auto"
                      }
            }
        };

        const pinnedColumns = keys.filter(key => updatedConfig[key]?.pinned);
        const columnLeftValues = getPinnedColumnLeftPositions(pinnedColumns, pinnedColumnsRef.current);

        pinnedColumns.forEach(key => {
            updatedConfig[key] = {
                ...updatedConfig[key],
                style: {
                    ...updatedConfig[key]?.style,
                    left: columnLeftValues[key] || "auto"
                }
            };
        });

        setTableData(prevTableData => ({
            ...prevTableData,
            config: updatedConfig
        }));
        await saveUserTableConfig(updatedConfig);
        updateTaskManagerEntityParams(updatedConfig);
        setTableConfigLoading(false);
    };

    const unPinAllColumn = async () => {
        const { config, keys } = tableData;
        const updatedConfig = { ...config };
        keys.forEach(key => {
            updatedConfig[key] = {
                ...updatedConfig[key],
                pinned: false,
                style: {
                    ...config[key]?.style,
                    left: "auto"
                }
            };
        });
        setTableData(prevTableData => ({
            ...prevTableData,
            config: updatedConfig
        }));
        await saveUserTableConfig(updatedConfig);
        updateTaskManagerEntityParams(updatedConfig);
    };

    const updateColumnWidthInConfig = (columnKey, style) => {
        const { keys, config } = tableData;
        const updatedConfig = { ...config };

        const pinnedColumns = keys.filter(key => updatedConfig[key]?.isVisible && updatedConfig[key]?.pinned);
        const columnLeftValues = getPinnedColumnLeftPositions(pinnedColumns, pinnedColumnsRef.current);

        pinnedColumns.forEach(key => {
            updatedConfig[key] = {
                ...updatedConfig[key],
                style: {
                    ...updatedConfig[key]?.style,
                    left: columnLeftValues[key] || "auto"
                }
            };
        });

        updatedConfig[columnKey] = {
            ...updatedConfig[columnKey],
            style: {
                ...updatedConfig[columnKey]?.style,
                ...style
            }
        };

        setTableData(prevTableData => ({
            ...prevTableData,
            config: updatedConfig
        }));
        saveUserTableConfig(updatedConfig);
        updateTaskManagerEntityParams(updatedConfig);
    };

    const isColunmVisibleChanged = () => {
        let flag = false;
        tableData.keys.forEach(key => {
            if (!isEqual(tableData.config[key]?.isVisible, generalTaskManagerTableData.config[key]?.isVisible)) {
                flag = true;
            }
        });
        return flag || tableConfigChanged;
    };

    const handleGoToColumn = gotoKey => {
        if (gotoKey) {
            setSearchField(gotoKey);
            setHighlightSearchField(gotoKey);
        }
    };

    const handleDeleteFloor = async id => {
        setShowConfirmDeleteModal(true);
        setSelectedDeleteTaskById(id);
    };

    const deleteTaskOnConfirm = async () => {
        await dispatch(actions.deleteTaskManagerById(selectedDeleteTaskById));
        deleteRef.current = true;
    };

    const renderdeleteByIdConfirmationModal = () => {
        if (!confirmDeleteModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={"Do you want to delete this Task?"}
                        message={"This action cannot be reverted, are you sure that you need to delete this item?"}
                        onNo={() => setShowConfirmDeleteModal(false)}
                        onYes={deleteTaskOnConfirm}
                    />
                }
                onCancel={() => setShowConfirmDeleteModal(false)}
            />
        );
    };

    const hasEndTask = async id => {
        setShowConfirmEndTask(true);
        setSelectedEndTask(id);
    };
    const onEndTaskManager = async () => {
        await dispatch(actions.cancelTaskManagerById(selectedEndTask));
        cancelRef.current = true;
    };
    const renderEndTaskConfirmationModal = () => {
        if (!showConfirmEndTask) return null;

        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={`Are you sure you want to End the task?`}
                        message={"Are you sure you want to proceed?"}
                        onNo={() => setShowConfirmEndTask(false)}
                        onYes={onEndTaskManager}
                        cancel={() => setShowConfirmEndTask(false)}
                    />
                }
                onCancel={() => setShowConfirmEndTask(false)}
            />
        );
    };
    const onHasLog = async id => {
        setShowLogModal(id);
    };

    const viewTaskLogFile = async task => {
        const logFileUrl = task?.log_file;
        const url = await getPresignedUrl(logFileUrl);

        if (!url) {
            setAlertMessage("Oops..! File url not found.");
            return;
        }

        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const downloadTaskLogFile = async task => {
        const logFileUrl = task?.log_file;

        if (!logFileUrl) {
            setAlertMessage("Oops..! File url not found.");
            return;
        }

        await downloadPresignedUrlFile(logFileUrl, "attachment", task?.log_file_name);
    };

    const renderTaskManagerLogModal = () => {
        if (!showLogModal) return null;

        return (
            <Portal
                body={
                    <ErrorLog
                        logData={tableData?.data?.find(item => item?.id === showLogModal)?.error_json}
                        onCancel={() => setShowLogModal(false)}
                    />
                }
                onCancel={() => {
                    setShowLogModal(false);
                }}
            />
        );
    };

    const renderDynamicColumnModal = () => {
        if (!showViewModal) return null;

        return (
            <Portal
                body={
                    <DynamicColumnModal
                        tableData={tableData}
                        handleHideColumn={handleHideColumn}
                        onCancel={() => setShowViewModal(false)}
                        handleGoToColumn={handleGoToColumn}
                    />
                }
                onCancel={() => setShowViewModal(false)}
            />
        );
    };

    return (
        <React.Fragment>
            <LoadingOverlay active={getTaskManagerListResponse?.isLoading || tableConfigLoading} spinner={<Loader />} fadeSpeed={10}>
                <div className="dtl-sec col-md-12">
                    <div className="table-top-menu allign-right">
                        <div className="rgt">
                            <TableTopIcons
                                tableParams={params}
                                hasSort
                                // resetAll={resetFilter}
                                tableData={tableData}
                                globalSearchKey={params.search}
                                handleGlobalSearch={handleGlobalSearch}
                                toggleWildCardFilter={toggleWildCardFilter}
                                resetSort={resetSort}
                                showWildCardFilter={showWildCardFilter}
                                hasWildCardFilter
                                resetAllFilters={resetTableAllFilter}
                                showViewModal={() => setShowViewModal(!showViewModal)}
                                hasNewlyCreated
                                hasNewlyEdited
                                updateLastSortFilter={updateLastSortFilter}
                                hasGoToFunctionality={true}
                                resetAllTableConfig={resetAllTableConfig}
                                isTableResetting={tableConfigLoading}
                                isColunmVisibleChanged={isColunmVisibleChanged}
                            />

                            <button
                                className="btn btn-edit btn-refresh mr-1"
                                onClick={e => {
                                    e.preventDefault();
                                    refreshTaskManagerList();
                                }}
                            >
                                <span className="icon mr-1">
                                    <img src={refreshIcon} alt="" />
                                </span>
                                <span className="text">Refresh</span>
                            </button>

                            <button
                                className="btn btn-edit btn-refresh mr-1"
                                onClick={e => {
                                    e.preventDefault();
                                    resetFilter();
                                }}
                            >
                                <span className="text">Reset Filters & Sort</span>
                            </button>
                        </div>
                    </div>
                    <div className="table-region">
                        <Table
                            noFutureDate
                            showWildCardFilter={showWildCardFilter}
                            updateWildCardFilter={updateWildCardFilter}
                            wildCardFilter={params?.filters}
                            isGeneralTaskManager
                            hasSearchApi={true}
                            tableData={tableData}
                            hasEdit={false}
                            updateSelectedRow={() => null}
                            hasDelete={checkPermission("forms", "task_manager", "delete")}
                            handleDeleteItem={handleDeleteFloor}
                            hasEndTask={hasEndTask}
                            showInfoPage={false}
                            hasInfoPage={false}
                            isNewTableFilters
                            getListForCommonFilter={getListForCommonFilter}
                            commonFilterData={getListForCommonFilterResponse}
                            updateCommonFilter={updateCommonFilter}
                            exportFilters={params?.exportParams}
                            commonFilter={params?.list}
                            updateTableSortFilters={updateTableSortFilters}
                            tableParams={params}
                            hasPadding={true}
                            commonTableClassName="overflow-hght"
                            hasErrorLog={onHasLog}
                            hasViewLog={viewTaskLogFile}
                            hasDownloadLog={downloadTaskLogFile}
                            handleHideColumn={handleHideColumn}
                            handleColumnPin={handleColumnPin}
                            pinnedColumnsRef={pinnedColumnsRef.current}
                            unPinAllColumn={unPinAllColumn}
                            updateColumnWidthInConfig={updateColumnWidthInConfig}
                            searchField={searchField}
                            setSearchField={() => setSearchField(null)}
                            highlightSearchField={highlightSearchField}
                            hasPin={true}
                            additionalTableClass="resize-icon-st"
                            tableRef={tableRef}
                        />

                        {tableData.data && tableData.data.length ? (
                            <div className="table-bottom d-flex">
                                <div className="count d-flex col-md-6">
                                    <div className="count-dtl">
                                        Total Count: <span>{paginationParams?.totalCount}</span>
                                    </div>
                                    <div className="col-md-2 pr-2 selbx">
                                        <select className="form-control" value={paginationParams.perPage} onChange={e => handlePerPageChange(e)}>
                                            <option value="10">10</option>
                                            <option value="20">20</option>
                                            <option value="30">30</option>
                                            <option value="40">40</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                            <option value="150">150</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="pagination-sec col-md-6">
                                    <ReactPaginate
                                        previousLabel={
                                            <span data-place="top" data-effect="solid" data-tip={"Previous"} data-background-color="#007bff">
                                                &lt;
                                            </span>
                                        }
                                        nextLabel={
                                            <span data-place="top" data-effect="solid" data-tip={"Next"} data-background-color="#007bff">
                                                &gt;
                                            </span>
                                        }
                                        pageCount={paginationParams?.totalPages}
                                        breakLabel={"..."}
                                        marginPagesDisplayed={2}
                                        pageRangeDisplayed={5}
                                        onPageChange={handlePageClick}
                                        containerClassName={"pagination"}
                                        subContainerClassName={"pages pagination"}
                                        activeClassName={"active"}
                                    />
                                    <ReactTooltip />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                {renderdeleteByIdConfirmationModal()}
                {renderEndTaskConfirmationModal()}
                {renderTaskManagerLogModal()}
                {renderDynamicColumnModal()}
            </LoadingOverlay>
        </React.Fragment>
    );
};

export default GeneralTaskManager;
